import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Factory, Users, Package, Clock, Printer } from "lucide-react";
import { toast } from "sonner";

const MONTHS = [
  "January","February","March","April","May","June","July","August","September","October","November","December",
];

function currentYearMonth() {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function KPI({ icon: Icon, label, value, sub, tint = "slate" }) {
  const tints = {
    brand: "text-brand", emerald: "text-emerald-700", blue: "text-blue-700", amber: "text-amber-700", slate: "text-slate-700", indigo: "text-indigo-700",
  };
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${tints[tint]}`} />
        <div className="text-[10.5px] uppercase tracking-widest font-semibold text-slate-500">{label}</div>
      </div>
      <div className="text-2xl font-heading font-bold mt-1">{value}</div>
      {sub && <div className="text-[10.5px] text-slate-400 mt-0.5">{sub}</div>}
    </Card>
  );
}

export default function MonthlyLineReport({ lines }) {
  const { year: y0, month: m0 } = currentYearMonth();
  const [year, setYear] = useState(y0);
  const [month, setMonth] = useState(m0);
  const [lineId, setLineId] = useState("__all__");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const printRef = useRef(null);

  const years = useMemo(() => {
    const s = new Set([y0 - 1, y0, y0 + 1, year]);
    return Array.from(s).sort();
  }, [y0, year]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { year, month };
      if (lineId && lineId !== "__all__") params.line_id = lineId;
      const { data } = await api.get("/line-tracking/monthly-report", { params });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load report");
      setData(null);
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, lineId]);

  const scope = lineId === "__all__" ? "All lines" : ((lines || []).find((l) => l.id === lineId)?.name || "Line");

  const handlePrint = () => {
    // Native browser print — only the report block via a print CSS class
    if (!printRef.current) return;
    const printContents = printRef.current.innerHTML;
    const w = window.open("", "_blank", "width=1024,height=768");
    if (!w) { toast.error("Popup blocked — allow popups to print"); return; }
    w.document.write(`<!doctype html><html><head><title>Monthly Report — ${MONTHS[month - 1]} ${year}</title>
      <style>
        body{ font-family: Arial, sans-serif; color:#0F172A; padding:24px; }
        h1{ font-size:20px; margin:0 0 4px 0 }
        h2{ font-size:14px; margin:16px 0 8px 0; color:#7C1D3A }
        .kpis{ display:grid; grid-template-columns: repeat(4, 1fr); gap:12px; margin: 16px 0 }
        .kpi{ border:1px solid #E5E7EB; border-radius:6px; padding:10px }
        .kpi .lbl{ font-size:10px; text-transform:uppercase; letter-spacing:.06em; color:#64748B }
        .kpi .val{ font-size:22px; font-weight:700; margin-top:4px }
        table{ width:100%; border-collapse: collapse; font-size: 12px }
        th, td { border-bottom:1px solid #E5E7EB; padding:6px 8px; text-align:left }
        th { background:#F8FAFC; font-weight:600 }
        .r { text-align:right; font-family: ui-monospace, Menlo, monospace }
        .muted{ color:#64748B; font-size:11px }
      </style></head><body>${printContents}</body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <div className="space-y-4" data-testid="monthly-report-tab">
      <Card className="p-3 border-slate-200 rounded-md bg-white">
        <div className="flex flex-wrap gap-2 items-center">
          <Calendar className="h-4 w-4 text-brand" />
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
            <SelectTrigger className="w-[110px]" data-testid="mr-year"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
            <SelectTrigger className="w-[150px]" data-testid="mr-month"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={lineId} onValueChange={setLineId}>
            <SelectTrigger className="w-[180px]" data-testid="mr-line"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All lines (rolled up)</SelectItem>
              {(lines || []).map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} data-testid="mr-refresh">Refresh</Button>
          <div className="flex-1" />
          <Button size="sm" onClick={handlePrint} className="bg-brand text-white hover:bg-brand-dark" data-testid="mr-print">
            <Printer className="h-4 w-4 mr-1.5" />Print / PDF
          </Button>
        </div>
      </Card>

      {loading ? (
        <div className="text-sm text-slate-500 text-center py-6">Loading…</div>
      ) : !data ? (
        <div className="text-sm text-slate-500 text-center py-6">No data.</div>
      ) : (
        <div ref={printRef}>
          <div>
            <h1 style={{ fontSize: 20, margin: 0 }} className="font-heading font-bold">Monthly Report — {MONTHS[month - 1]} {year}</h1>
            <div className="muted text-[12.5px] text-slate-500">Scope: <b>{scope}</b> · {data.period.days_in_month} days in month · Sundays counted as holidays: <b>{data.auto_sundays_off ? "Yes" : "No"}</b></div>
          </div>

          <div className="kpis grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <KPI icon={Calendar} label="Working days" value={data.working_days} tint="emerald" sub="full-month plan" />
            <KPI icon={Clock} label="Holidays (incl. Sundays)" value={data.holiday_count} tint="brand" sub="full-month plan" />
            <KPI icon={Package} label="Total output (pcs)" value={Number(data.total_output || 0).toLocaleString()} tint="blue" sub="month to date" />
            <KPI icon={Users} label="Avg. manpower / day" value={data.avg_manpower} tint="indigo"
              sub={data.manpower_basis_days != null ? `over ${data.manpower_basis_days} worked day(s) × ${data.line_id ? 1 : "all lines"}` : undefined} />
          </div>

          <h2 className="font-heading font-semibold text-[14px] mt-6 mb-2 text-brand">SOs produced this month ({data.so_breakdown.length})</h2>
          <div className="overflow-x-auto">
            <table className="opsiq-table w-full">
              <thead>
                <tr>
                  <th>SO #</th><th>Buyer</th><th>Buyer Style</th><th>BBS Style</th>
                  <th className="text-right">Ord Qty</th><th className="text-right">Produced this month</th><th className="text-right">Produced till date</th><th className="text-right">Share of month</th>
                </tr>
              </thead>
              <tbody>
                {data.so_breakdown.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-4 text-slate-500 text-[12.5px]">No output recorded in this month.</td></tr>
                ) : data.so_breakdown.map((s) => {
                  const share = data.total_output > 0 ? Math.round((s.qty_produced_month / data.total_output) * 100) : 0;
                  return (
                    <tr key={s.order_number} data-testid={`mr-so-${s.order_number}`}>
                      <td className="font-mono font-semibold text-brand">{s.order_number}</td>
                      <td>{s.buyer_name}</td>
                      <td className="font-mono text-[12.5px]">{s.buyer_style}</td>
                      <td className="font-mono text-[12.5px]">{s.bbs_style || "—"}</td>
                      <td className="text-right font-mono">{Number(s.order_qty || 0).toLocaleString()}</td>
                      <td className="text-right font-mono font-bold">{Number(s.qty_produced_month || 0).toLocaleString()}</td>
                      <td className="text-right font-mono">{Number(s.produced_till_date || 0).toLocaleString()}</td>
                      <td className="text-right font-mono">{share}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {data.explicit_holidays.length > 0 && (
            <>
              <h2 className="font-heading font-semibold text-[14px] mt-6 mb-2 text-brand">Holidays in month ({data.explicit_holidays.length})</h2>
              <div className="overflow-x-auto">
                <table className="opsiq-table w-full">
                  <thead><tr><th>Date</th><th>Name</th></tr></thead>
                  <tbody>
                    {data.explicit_holidays.map((h) => (
                      <tr key={h.id}><td className="font-mono">{h.date}</td><td>{h.name}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data.auto_sundays_off && <div className="text-[11px] text-slate-500 mt-1.5">Sundays ({data.sundays_count}) are also treated as non-working days.</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
